<script lang="ts">
  import { api } from '../../lib/api';
  import { normalizeTallyProvider } from '../../../../shared/src/defaults';
  import type { TallyProviderConfig } from '../../../../shared/src/types';
  import { clone, type Overview } from './types';

  let { data, reload }: { data: Overview; reload: () => void } = $props();
  let draft = $state<TallyProviderConfig | null>(null);
  let error = $state<string | null>(null);

  function edit(t: TallyProviderConfig | null) {
    error = null;
    draft = t ? clone(t) : normalizeTallyProvider({ kind: 'atem', name: 'ATEM' });
  }
  function setKind(kind: TallyProviderConfig['kind']) {
    if (draft) draft = normalizeTallyProvider({ id: draft.id, name: draft.name, enabled: draft.enabled, kind });
  }
  async function save() {
    if (!draft) return;
    try {
      await api.post('/api/admin/tally', draft);
      draft = null;
      reload();
    } catch (err) {
      error = (err as Error).message;
    }
  }
  async function remove(t: TallyProviderConfig) {
    if (!confirm(`Delete ${t.name}? Positions mapped to it will show TALLY DISCONNECTED.`)) return;
    await api.del(`/api/admin/tally/${t.id}`);
    reload();
  }
  const status = (id: string) => data.tallyStatus.find((s) => s.id === id);
</script>

<div class="panel">
  <div class="row"><h3 class="grow">Tally providers</h3><button class="primary" onclick={() => edit(null)}>Add provider</button></div>
  {#each data.tallyProviders as t (t.id)}
    {@const s = status(t.id)}
    <div class="list-item">
      <b class="grow">{t.name}</b>
      <span class="badge">{t.kind}{t.host ? ` ${t.host}:${t.port}` : t.port ? ` :${t.port}` : ''}{t.kind === 'atem' ? ` M/E ${(t.me ?? 0) + 1}` : ''}</span>
      {#if !t.enabled}<span class="badge">disabled</span>
      {:else if s}<span class="badge {s.connected ? 'ok' : 'bad'}">{s.connected ? 'connected' : 'down'}</span><span class="muted">{s.detail}</span>{/if}
      <button class="small" onclick={() => edit(t)}>Edit</button>
      <button class="small danger" onclick={() => remove(t)}>Delete</button>
    </div>
  {:else}<p class="muted">No tally providers.</p>{/each}
</div>

{#if draft}
  <div class="panel" style="margin-top:12px">
    <div class="form">
      <label>Name <input bind:value={draft.name} /></label>
      <label>Type
        <select value={draft.kind} onchange={(e) => setKind(e.currentTarget.value as TallyProviderConfig['kind'])}>
          <option value="atem">Blackmagic ATEM</option><option value="vmix">vMix (TCP API)</option>
          <option value="tsl31">TSL UMD 3.1 listener</option><option value="tsl5">TSL UMD 5.0 listener</option><option value="mock">Mock switcher</option>
        </select>
      </label>
      <label class="inline"><input type="checkbox" bind:checked={draft.enabled} /> Enabled</label>
      {#if draft.kind === 'atem' || draft.kind === 'vmix'}
        <label>Host / IP <input bind:value={draft.host} placeholder="192.168.10.240" /></label>
        <label>Port <input type="number" bind:value={draft.port} /></label>
      {/if}
      {#if draft.kind === 'atem'}
        <label>Mix effect
          <select bind:value={draft.me}>{#each [0, 1, 2, 3] as m (m)}<option value={m}>M/E {m + 1}</option>{/each}</select>
        </label>
        <p class="wide muted">Program includes inputs visible through upstream/downstream keys and SuperSource boxes, as on the ATEM multiviewer.</p>
      {/if}
      {#if draft.kind === 'tsl31' || draft.kind === 'tsl5'}
        <label>Listen port <input type="number" bind:value={draft.port} /></label>
        <label>Transport <select bind:value={draft.transport}><option>udp</option><option>tcp</option></select></label>
        <label>{draft.kind === 'tsl31' ? 'Program = tally bit' : 'Program = colour'}
          <select bind:value={draft.tslProgram}>
            {#if draft.kind === 'tsl31'}{#each [1, 2, 3, 4] as b (b)}<option value={b}>Tally {b}</option>{/each}
            {:else}<option value={1}>Red</option><option value={2}>Green</option><option value={3}>Amber</option>{/if}
          </select>
        </label>
        <label>{draft.kind === 'tsl31' ? 'Preview = tally bit' : 'Preview = colour'}
          <select bind:value={draft.tslPreview}>
            {#if draft.kind === 'tsl31'}{#each [1, 2, 3, 4] as b (b)}<option value={b}>Tally {b}</option>{/each}
            {:else}<option value={1}>Red</option><option value={2}>Green</option><option value={3}>Amber</option>{/if}
          </select>
        </label>
        <label>Stale after (s, 0 = never) <input type="number" bind:value={draft.staleAfterSec} /></label>
        <p class="wide muted">Map positions to the TSL display address/index (decimal).</p>
      {/if}
      {#if draft.kind === 'mock' && draft.mock}
        <label>Inputs <input type="number" bind:value={draft.mock.inputs} /></label>
        <label>Auto-cycle seconds (0 = off) <input type="number" bind:value={draft.mock.autoCycleSec} /></label>
      {/if}
    </div>
    {#if error}<p class="error">{error}</p>{/if}
    <div class="row" style="margin-top:12px"><button class="primary" onclick={save}>Save &amp; apply</button><button onclick={() => (draft = null)}>Cancel</button></div>
  </div>
{/if}
